/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
/* HustleEats Phase 2.E — Event-driven deals tab.
   List nearby events + one-tap "accept suggestion" to publish an offer. */
export default function EventsTab({ venueSlug }) {
  const [events, setEvents] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "", category: "afl", venue_name: "",
    lat: "", lng: "", starts_at: "", ends_at: "", expected_attendance: 0, notes: "",
  });
  const [msg, setMsg] = useState(null);

  const refresh = async () => {
    const [eRes, sRes] = await Promise.all([
      axios.get(`${API}/api/hustleeats/events/near/${venueSlug}?radius_km=2.5&days_ahead=30`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/api/hustleeats/events/suggestions/${venueSlug}`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]);
    setEvents(eRes.data || []);
    setSuggestions(sRes.data || []);
  };

  useEffect(() => { refresh(); }, [venueSlug]);

  const createEvent = async (e) => {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    setMsg(null);
    try {
      const body = {
        ...form,
        lat: Number(form.lat),
        lng: Number(form.lng),
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: new Date(form.ends_at).toISOString(),
        expected_attendance: Number(form.expected_attendance),
      };
      await axios.post(`${API}/api/hustleeats/events/`, body, { withCredentials: true });
      setMsg({ kind: "ok", text: "Event added — refreshing nearby events." });
      setForm({ ...form, name: "", venue_name: "" });
      refresh();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Failed." });
    } finally {
      setCreating(false);
    }
  };

  const suggestDeal = async (eventId) => {
    try {
      await axios.post(
        `${API}/api/hustleeats/events/${eventId}/suggest-deal/${venueSlug}`,
        {}, { withCredentials: true },
      );
      setMsg({ kind: "ok", text: "Suggestion generated below." });
      refresh();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Failed." });
    }
  };

  const actOnSuggestion = async (sugId, accepted) => {
    try {
      const { data } = await axios.post(
        `${API}/api/hustleeats/events/suggestions/${sugId}/act`,
        { accepted }, { withCredentials: true },
      );
      setMsg({
        kind: "ok",
        text: accepted ? `Offer published! id=${data.offer_id}` : "Suggestion rejected.",
      });
      refresh();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Failed." });
    }
  };

  return (
    <div className="ph2-tabbody">
      <h3 className="ph2-h3">Add an event</h3>
      <form onSubmit={createEvent} className="he-form">
        <div className="he-form__row">
          <label className="he-form__field">
            <span>Event name</span>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ev-name" />
          </label>
          <label className="he-form__field">
            <span>Category</span>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              <option value="afl">AFL</option>
              <option value="nrl">NRL</option>
              <option value="concert">Concert</option>
              <option value="other_sport">Other sport</option>
              <option value="theatre">Theatre</option>
              <option value="festival">Festival</option>
            </select>
          </label>
        </div>
        <label className="he-form__field">
          <span>Venue name (stadium, hall etc.)</span>
          <input required value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })} />
        </label>
        <div className="he-form__row">
          <label className="he-form__field"><span>Lat</span><input type="number" step="any" required value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="-27.4648" /></label>
          <label className="he-form__field"><span>Lng</span><input type="number" step="any" required value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="153.0094" /></label>
          <label className="he-form__field"><span>Expected attendance</span><input type="number" value={form.expected_attendance} onChange={(e) => setForm({ ...form, expected_attendance: e.target.value })} /></label>
        </div>
        <div className="he-form__row">
          <label className="he-form__field"><span>Starts</span><input type="datetime-local" required value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} /></label>
          <label className="he-form__field"><span>Ends</span><input type="datetime-local" required value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} /></label>
        </div>
        <button type="submit" className="he-card__cta" disabled={creating} data-testid="ev-create-btn">
          {creating ? "Adding…" : "Add event"}
        </button>
      </form>
      {msg && <div className={`he-admin__msg he-admin__msg--${msg.kind}`}>{msg.text}</div>}

      <h3 className="ph2-h3" style={{ marginTop: 32 }}>Nearby upcoming events</h3>
      {events.length === 0 && <div className="he-state">No events within 2.5km — add one above (venue location must be set for matching).</div>}
      {events.map((ev) => (
        <div key={ev.id} className="ph2-event" data-testid={`ev-near-${ev.id}`}>
          <div>
            <div className="ph2-event__name">{ev.name}</div>
            <div className="ph2-event__sub">
              {ev.venue_name} · {new Date(ev.starts_at).toLocaleString("en-AU", { timeZone: "Australia/Brisbane" })} · {ev.distance_km}km away
            </div>
          </div>
          <button className="ph2-btn" onClick={() => suggestDeal(ev.id)}>Suggest a deal</button>
        </div>
      ))}

      <h3 className="ph2-h3" style={{ marginTop: 32 }}>Suggestions</h3>
      {suggestions.length === 0 && <div className="he-state">No suggestions yet.</div>}
      {suggestions.map((s) => (
        <div key={s.id} className="ph2-suggestion" data-testid={`ev-sug-${s.id}`}>
          <div>
            <div className="ph2-event__name">{s.suggested_title}</div>
            <div className="ph2-event__sub">{s.suggested_blurb}</div>
            <div className="ph2-event__sub" style={{ marginTop: 6 }}>
              ${(s.suggested_credit_cents / 100).toFixed(2)} credit · cap {s.suggested_max_covers} · status: {s.status}
            </div>
          </div>
          {s.status === "pending" && (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="ph2-btn" onClick={() => actOnSuggestion(s.id, true)} data-testid={`ev-sug-accept-${s.id}`}>Publish</button>
              <button className="ph2-btn ph2-btn--ghost" onClick={() => actOnSuggestion(s.id, false)}>Reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
